import React, { ReactNode, ReactElement } from 'react';
import { withOptimizely } from '@optimizely/react-sdk';
import { Experiments, ExperimentVariantsValues } from 'app/types/optimizely';
import { checkExpirationDate } from 'app/lib/optimizely';
import { VariationProps } from 'app/components/Experiment/ExperimentVariation';
import { WithOptimizelyProps } from '@optimizely/react-sdk/dist/withOptimizely';
import { isClient } from 'app/lib/utils';

interface ExperimentProps<E extends Experiments> extends WithOptimizelyProps {
  name: E;
  children: ReactNode | ChildrenRenderFunction<E>;
  timeout?: number;
}

type ChildrenRenderFunction<E extends Experiments> = (variation: ExperimentVariantsValues<E> | null) => ReactNode;

/**
 * Experiment component allows to evaluate which variation a user gets in an experiment
 */
export const Experiment = withOptimizely(
  <E extends Experiments>({ name, children, optimizely }: ExperimentProps<E>) => {
    // use activate on clients, and getVariation on server, as we dont want to send this event to optimizely dashboard
    const variation = ((isClient()
      ? optimizely?.activate(name)
      : optimizely?.getVariation(name)) as unknown) as ExperimentVariantsValues<E>;
    if (!optimizely) {
      console.error('Looks like you forgot to wrap component with withOptimizely from app/components/withOptimizely'); // eslint-disable-line no-console
    }

    checkExpirationDate('Experiments', name);

    if (typeof children === 'function') {
      return children(variation);
    }

    let match: ReactElement<VariationProps<E>> | null = null;
    let defaultVariant: ReactElement<VariationProps<E>> | null = null;

    React.Children.forEach(children, (child: ReactElement<VariationProps<E>>) => {
      if (match || !React.isValidElement(child)) {
        return;
      }

      if (child.props.variation) {
        if (Array.isArray(child.props.variation)) {
          const foundVariation = child.props.variation.find((v) => v === variation);

          if (foundVariation) {
            match = child;
          }

          return;
        }

        if (variation === child.props.variation) {
          match = child;
        }
      }

      if (child.props.default) {
        defaultVariant = child;
      }
    });

    match = match || defaultVariant;

    return match ? React.cloneElement(match, { variation }) : null;
  }
);
